import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, MoreVertical, Building2, Users, CheckCircle, XCircle, AlertCircle, Copy, Ban, RefreshCw, Archive } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import CompanyDetailsModal from './components/CompanyDetailsModal';
import SuspendCompanyModal from './components/SuspendCompanyModal';
import AddCompanyModal from './components/AddCompanyModal';
import DeletionPreviewModal from './components/DeletionPreviewModal';
import { getAllCompanies } from '../../../services/companyService';

const CompanyManagementPortal = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeletionPreviewOpen, setIsDeletionPreviewOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  // Supabase integration
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);

  // Toast state
  const [toast, setToast] = useState(null);

  // Load companies from Supabase on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getAllCompanies();

      if (result?.success) {
        setCompanies(result?.data);
      } else {
        setError(result?.error || 'Failed to load companies');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred while loading companies');
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies?.filter(company => {
    const matchesSearch = company?.companyName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      company?.companyTag?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      company?.primaryOwner?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesStatus = filterStatus === 'all' || company?.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const handleOpenCompany = (company) => {
    setSelectedCompany(company);
    setIsDetailsModalOpen(true);
  };

  const handleSuspendCompany = (company) => {
    setOpenDropdownId(null);
    setSelectedCompany(company);
    setIsSuspendModalOpen(true);
  };

  const handleCopyTag = async (tag) => {
    try {
      await navigator.clipboard.writeText(tag);
      setToast('Company tag copied to clipboard');
      setOpenDropdownId(null);
    } catch {
      setToast('Failed to copy to clipboard');
    }
  };

  const toggleDropdown = (companyId) => {
    setOpenDropdownId(openDropdownId === companyId ? null : companyId);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/10 text-green-400 border-green-500/20',
      suspended: 'bg-red-500/10 text-red-400 border-red-500/20',
      inactive: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
      deleted: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
    };

    const icons = {
      active: <CheckCircle className="w-3 h-3" />,
      suspended: <XCircle className="w-3 h-3" />,
      inactive: <AlertCircle className="w-3 h-3" />,
      deleted: <Archive className="w-3 h-3" />
    };

    const labels = {
      active: 'Active',
      suspended: 'Suspended',
      inactive: 'Inactive',
      deleted: 'Archived'
    };

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${styles?.[status] || styles.inactive}`}>
        {icons?.[status] || icons.inactive}
        {labels?.[status] || status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const handleOpenDeletedCompany = (company) => {
    setSelectedCompany(company);
    setIsDeletionPreviewOpen(true);
  };

  return (
    <>
      <Header />
      <Sidebar />
      <div className="min-h-screen bg-background pt-[73px] pl-0 lg:pl-60">
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-[26px] font-semibold text-text-primary mb-2">Company Management Portal</h1>
            <p className="text-text-secondary text-sm">Comprehensive administrative controls and analytics for all platform organizations</p>
          </div>

          {/* Toolbar */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
              <input
                type="text"
                placeholder="Search companies by name, tag, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-4 py-2.5 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
                <option value="deleted">Archived</option>
              </select>

              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Company
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
              <button
                onClick={loadCompanies}
                className="mt-2 text-orange-400 hover:text-orange-300 text-sm underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="bg-background border border-border rounded-lg p-12">
              <div className="flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mb-4"></div>
                <p className="text-text-secondary text-sm">Loading companies...</p>
              </div>
            </div>
          ) : (
            /* Companies Table */
            (<div className="bg-background border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Company</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Primary Owner</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Users</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Jobs</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Created</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredCompanies?.map((company) => (
                      <tr key={company?.id} className="hover:bg-[#222] transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="text-text-primary font-medium">{company?.companyName}</div>
                              <div className="text-text-secondary text-sm">{company?.companyTag}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-text-primary text-sm">{company?.primaryOwner?.fullName || 'N/A'}</div>
                            <div className="text-text-secondary text-xs">{company?.primaryOwner?.email || 'N/A'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-text-secondary" />
                            <span className="text-text-primary">{company?.userCount || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-text-primary">{company?.jobCount || 0}</td>
                        <td className="px-6 py-4">{getStatusBadge(company?.status)}</td>
                        <td className="px-6 py-4 text-text-secondary text-sm">
                          {new Date(company?.createdAt)?.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenCompany(company)}
                              className="px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-md text-sm font-medium transition-colors"
                            >
                              Open
                            </button>
                            <div className="relative" ref={openDropdownId === company?.id ? dropdownRef : null}>
                              <button
                                onClick={() => toggleDropdown(company?.id)}
                                className="p-2 hover:bg-surface rounded-md transition-colors"
                              >
                                <MoreVertical className="w-4 h-4 text-text-secondary" />
                              </button>
                              {openDropdownId === company?.id && (
                                <div className="absolute right-0 mt-2 w-48 bg-[#1a1a1a] border border-border rounded-lg shadow-xl z-20 py-1">
                                  <button
                                    onClick={() => handleCopyTag(company?.companyTag)}
                                    className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-surface flex items-center gap-3 transition-colors"
                                  >
                                    <Copy className="w-4 h-4 text-text-secondary" />
                                    Copy Company Tag
                                  </button>
                                  <div className="border-t border-border my-1" />
                                  {company?.status === 'deleted' ? (
                                    <button
                                      onClick={() => {
                                        setOpenDropdownId(null);
                                        handleOpenDeletedCompany(company);
                                      }}
                                      className="w-full px-4 py-2.5 text-left text-sm text-yellow-400 hover:bg-yellow-500/10 flex items-center gap-3 transition-colors"
                                    >
                                      <Archive className="w-4 h-4" />
                                      Manage Archived
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleSuspendCompany(company)}
                                      className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                                        company?.status === 'active'
                                          ? 'text-red-400 hover:bg-red-500/10'
                                          : 'text-green-400 hover:bg-green-500/10'
                                      }`}
                                    >
                                      {company?.status === 'active' ? (
                                        <>
                                          <Ban className="w-4 h-4" />
                                          Suspend Company
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="w-4 h-4" />
                                          Reactivate Company
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredCompanies?.length === 0 && !loading && (
                <div className="py-12 text-center">
                  <Building2 className="w-12 h-12 text-text-tertiary mx-auto mb-3" />
                  <p className="text-text-secondary text-sm">
                    {searchTerm || filterStatus !== 'all' ? 'No companies found matching your criteria' : 'No companies available'}
                  </p>
                </div>
              )}
            </div>)
          )}
        </main>

        {/* Toast Notification */}
        {toast && (
          <div className="fixed bottom-4 right-4 bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {toast}
          </div>
        )}

        {/* Modals */}
        {isDetailsModalOpen && selectedCompany && (
          <CompanyDetailsModal
            company={selectedCompany}
            onClose={() => {
              setIsDetailsModalOpen(false);
              setSelectedCompany(null);
            }}
            onSuspend={() => {
              setIsDetailsModalOpen(false);
              setIsSuspendModalOpen(true);
            }}
            onRefresh={loadCompanies}
          />
        )}
        {isSuspendModalOpen && selectedCompany && (
          <SuspendCompanyModal
            company={selectedCompany}
            onClose={() => {
              setIsSuspendModalOpen(false);
              setSelectedCompany(null);
            }}
            onSuccess={() => {
              setIsSuspendModalOpen(false);
              setSelectedCompany(null);
              loadCompanies(); // Refresh companies list
            }}
          />
        )}
        {isAddModalOpen && (
          <AddCompanyModal
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={() => {
              setIsAddModalOpen(false);
              loadCompanies(); // Refresh companies list
            }}
          />
        )}
        {isDeletionPreviewOpen && selectedCompany && (
          <DeletionPreviewModal
            company={selectedCompany}
            onClose={() => {
              setIsDeletionPreviewOpen(false);
              setSelectedCompany(null);
            }}
            onSuccess={() => {
              setIsDeletionPreviewOpen(false);
              setSelectedCompany(null);
              loadCompanies(); // Refresh companies list
            }}
          />
        )}
      </div>
    </>
  );
};

export default CompanyManagementPortal;
